<template>
  <div class="skeleton-presets">
    <!-- 首页骨架屏 -->
    <SkeletonScreen v-if="preset === 'home'" :loading="loading" :animated="animated" variant="custom"
      :category-count="categoryCount" />

      <SkeletonCard v-else-if="preset === 'card'" :loading="loading" :animated="animated" variant="card"
        :category-count="categoryCount" />
     
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import SkeletonScreen from './SkeletonScreen.vue'
import SkeletonCard from './SkeletonCard.vue'
import type { SkeletonItem } from './SkeletonScreen.vue'
import theme from '@/styles/theme'

// 预设类型
export type SkeletonPreset =
  | 'home'
  | 'list'
  | 'detail'
  | 'profile'
  | 'chat'
  | 'grid'
  | 'card'
  | 'search'
  | 'message'
  | 'store'
  | 'category'
// 组件属性
interface Props {
  preset: SkeletonPreset
  loading?: boolean
  animated?: boolean
  listCount?: number
  chatCount?: number
  gridColumns?: number
  gridCount?: number
  avatarSize?: string | number
  categoryCount?: number
}

const props = withDefaults(defineProps<Props>(), {
  loading: true,
  animated: true,
  listCount: 5,
  chatCount: 6,
  gridColumns: 2,
  gridCount: 6,
  avatarSize: 40,
  categoryCount: 5
})

// 首页骨架屏配置
const homeItems = computed<SkeletonItem[]>(() => [
  {
    title: false,
    row: 0,
    avatar: false,
    rowWidth: '100%',
    rowHeight: '200px',
    className: 'skeleton-banner'
  },
  {
    title: true,
    row: 1,
    avatar: false,
    rowWidth: '60%',
    className: 'skeleton-section-title'
  },
  {
    title: false,
    row: 0,
    avatar: false,
    rowWidth: '100%',
    rowHeight: '120px',
    className: 'skeleton-category'
  },
  {
    title: true,
    row: 1,
    avatar: false,
    rowWidth: '40%',
    className: 'skeleton-section-title'
  },
  {
    title: false,
    row: 0,
    avatar: false,
    rowWidth: '100%',
    rowHeight: '150px',
    className: 'skeleton-content'
  }
])

// 搜索页骨架屏配置
const searchItems = computed<SkeletonItem[]>(() => [
  {
    title: false,
    row: 0,
    avatar: false,
    rowWidth: '100%',
    rowHeight: '40px',
    className: 'skeleton-search-bar'
  },
  {
    title: true,
    row: 1,
    avatar: false,
    rowWidth: '30%',
    className: 'skeleton-filter-title'
  },
  {
    title: false,
    row: 0,
    avatar: false,
    rowWidth: '100%',
    rowHeight: '60px',
    className: 'skeleton-filter-tags'
  },
  {
    title: true,
    row: 1,
    avatar: false,
    rowWidth: '50%',
    className: 'skeleton-results-title'
  },
  {
    title: true,
    row: 2,
    avatar: true,
    avatarSize: 48,
    className: 'skeleton-result-item'
  },
  {
    title: true,
    row: 2,
    avatar: true,
    avatarSize: 48,
    className: 'skeleton-result-item'
  }
])

// 消息页骨架屏配置
const messageItems = computed<SkeletonItem[]>(() => [
  {
    title: true,
    row: 1,
    avatar: true,
    avatarSize: 48,
    className: 'skeleton-message-item'
  },
  {
    title: true,
    row: 1,
    avatar: true,
    avatarSize: 48,
    className: 'skeleton-message-item'
  },
  {
    title: true,
    row: 1,
    avatar: true,
    avatarSize: 48,
    className: 'skeleton-message-item'
  },
  {
    title: true,
    row: 1,
    avatar: true,
    avatarSize: 48,
    className: 'skeleton-message-item'
  },
  {
    title: true,
    row: 1,
    avatar: true,
    avatarSize: 48,
    className: 'skeleton-message-item'
  }
])

// 商店页骨架屏配置
const storeItems = computed<SkeletonItem[]>(() => [
  {
    title: false,
    row: 0,
    avatar: false,
    rowWidth: '100%',
    rowHeight: '180px',
    className: 'skeleton-banner'
  },
  {
    title: true,
    row: 1,
    avatar: false,
    rowWidth: '40%',
    className: 'skeleton-category-title'
  },
  {
    title: false,
    row: 0,
    avatar: false,
    rowWidth: '100%',
    rowHeight: '100px',
    className: 'skeleton-category-list'
  },
  {
    title: true,
    row: 1,
    avatar: false,
    rowWidth: '50%',
    className: 'skeleton-products-title'
  },
  {
    title: false,
    row: 0,
    avatar: false,
    rowWidth: '100%',
    rowHeight: '200px',
    className: 'skeleton-product-grid'
  }
])
</script>

<style scoped lang="scss">
.skeleton-presets {
  width: 100%;
}

// 首页骨架屏特殊样式
:deep(.skeleton-banner) {
  margin-bottom: 16px;
  border-radius: v-bind('theme.borderRadius.large');
  padding: 0 !important;
}

:deep(.van-skeleton.van-skeleton--animate) {
  padding: 0 !important;
  border-radius: v-bind('theme.borderRadius.large');

}

:deep(.skeleton-section-title) {
  margin: 16px 0 12px 0;
}

:deep(.skeleton-category) {
  margin-bottom: 20px;
  border-radius: v-bind('theme.borderRadius.medium');
}

:deep(.skeleton-content) {
  border-radius: v-bind('theme.borderRadius.medium');
}

// 搜索页骨架屏特殊样式
:deep(.skeleton-search-bar) {
  margin-bottom: 20px;
  border-radius: v-bind('theme.borderRadius.large');
}

:deep(.skeleton-filter-title) {
  margin: 16px 0 8px 0;
}

:deep(.skeleton-filter-tags) {
  margin-bottom: 20px;
  border-radius: v-bind('theme.borderRadius.medium');
}

:deep(.skeleton-results-title) {
  margin: 16px 0 12px 0;
}

:deep(.skeleton-result-item) {
  margin-bottom: 12px;
  padding: 12px;
  background: v-bind('theme.colors.backgroundSecondary');
  border-radius: v-bind('theme.borderRadius.medium');
}

// 消息页骨架屏特殊样式
:deep(.skeleton-message-item) {
  margin-bottom: 12px;
  padding: 12px;
  background: v-bind('theme.colors.backgroundSecondary');
  border-radius: v-bind('theme.borderRadius.medium');
}

// 商店页骨架屏特殊样式
:deep(.skeleton-category-title) {
  margin: 16px 0 8px 0;
}

:deep(.skeleton-category-list) {
  margin-bottom: 20px;
  border-radius: v-bind('theme.borderRadius.medium');
}

:deep(.skeleton-products-title) {
  margin: 16px 0 12px 0;
}

:deep(.skeleton-product-grid) {
  border-radius: v-bind('theme.borderRadius.medium');
}
</style>
